import React, { useMemo, useRef } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Row } from '../row';
import { Cell } from '../cell';
import { ColorPalette } from '../config';
import { TooltipLink } from './tooltip-link';
import { IChatMessage } from '../../hooks/ai-search/use-chat';
import { ChatCitationSummary } from './chat-citation-summary';
import { ChatFeedback } from './chat-feedback';
import { preprocessMessageContent } from './preprocess-message-content';

export type IChatMessageComponent = IChatMessageContentProps;

export function ChatMessageComponent(props: IChatMessageComponent) {
  const { isAssistant } = props;
  return (
    <Row>
      <Cell size="full">
        <Box
          component="section"
          tabIndex={0}
          sx={{
            fontSize: '14px',
            padding: '5px',
            borderRadius: '4px',
            position: 'relative',
            left: '10px',
            maxWidth: 'calc(100% - 50px)',
            minWidth: 'calc(100% - 50px)',
            letterSpacing: '0.2px',
            backgroundColor: isAssistant ? ColorPalette.ApexTintedLight : '#FAFAFA',
            border: isAssistant ? 'solid 1px #CCC' : 'solid 1px #F2F2F2',
            float: isAssistant ? 'right' : 'left',
            color: 'black',
            display: 'block',
            textAlign: 'left',
            height: 'auto',
          }}
        >
          <ChatMessageContent {...props} />
        </Box>
      </Cell>
    </Row>
  );
}

interface IChatMessageContentProps {
  message: IChatMessage;
  isAssistant: boolean;
  onChatFeedbackChange: (msg: IChatMessage) => Promise<void>;
  showCitations?: boolean;
}

function ChatMessageContent(props: IChatMessageContentProps) {
  const { message, onChatFeedbackChange, isAssistant, showCitations } = props;

  const infoPrefix = isAssistant ? 'Besvaret af' : 'Oprettet af';
  const draftLabel = isAssistant ? 'Svar på kladde' : 'Spørgsmål fra kladde';
  const messageContentRef = useRef<HTMLDivElement>(null);

  const preprocessed = useMemo(
    () => preprocessMessageContent(message),
    [message, message.content, message.citations]
  );

  return (
    <>
      <Box>
        {message.userName && message.timestamp && (
          <Box sx={{ textAlign: 'right', fontWeight: 'bolder', marginTop: 2, marginBottom: 2 }}>
            {message.isDraftStandardQuestion && <Chip label={draftLabel} size="small" sx={{ mr: 1 }} />}
            {isAssistant && message.previewModelName && <Chip label={`Preview – ${message.previewModelName}`} size="small" color="info" sx={{ mr: 1 }} />}
            {`${infoPrefix} ${message.userName} d. ${message.timestamp}`}
          </Box>
        )}
      </Box>
      <Box m={1}>
        {isAssistant && message.isPreparingReply ? (
          <Box role="status" aria-busy="true" aria-label="Forbereder svar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} aria-hidden="true" />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {message.preparationStatusMessage || 'Forbereder svar...'}
            </Typography>
          </Box>
        ) : (
          <div ref={messageContentRef}>
            <ReactMarkdown
              components={{
                a: ({ node, href, children }) => TooltipLink({ node, href, children, chatMessage: message }),
              }}
            >
              {preprocessed.content}
            </ReactMarkdown>
            {showCitations && <ChatCitationSummary citations={preprocessed.citations ?? []} />}
          </div>
        )}
        <ChatFeedback msg={message} onChatFeedbackChange={onChatFeedbackChange} messageContentRef={messageContentRef} />
      </Box>
    </>
  );
}
